import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TONE = { "Pending Approval": "warning", Approved: "success", Rejected: "danger" };

const splitColumns = (label) => [
  { key: "name", label, render: (r) => <span className="text-sm font-semibold">{r.name}</span> },
  { key: "Demo", label: "Demos", align: "right", render: (r) => <span className="num text-sm">{money(r.Demo, true)}</span> },
  { key: "Training", label: "Trainings", align: "right", render: (r) => <span className="num text-sm">{money(r.Training, true)}</span> },
  { key: "total", label: "Total", align: "right", render: (r) => <span className="num text-sm font-bold">{money(r.total, true)}</span> },
  { key: "approved", label: "Approved", align: "right", render: (r) => <span className="num text-sm text-success">{money(r.approved, true)}</span>, hideMobile: true },
  { key: "pending", label: "Pending", align: "right", render: (r) => <span className="num text-sm text-warning">{money(r.pending, true)}</span>, hideMobile: true },
  { key: "count", label: "Bills", align: "right", render: (r) => <span className="num text-sm">{r.count}</span>, hideMobile: true },
];

/** Visit spend for a month, a year or any window, with demos and trainings kept apart. */
export const DemoExpenseReport = () => {
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demos/expenses/summary", {
        params: period === "custom"
          ? { period, date_from: custom.from || undefined, date_to: custom.to || undefined }
          : { period },
      });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [period, custom]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const t = data?.totals || {};
  const billColumns = [
    { key: "date", label: "Date", render: (r) => <span className="text-sm">{fdate(r.date)}</span> },
    {
      key: "category", label: "Expense",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.category}</p>
          <p className="truncate text-xs text-muted-foreground">{[r.vendor, r.description].filter(Boolean).join(" \u00b7 ")}</p>
        </div>
      ),
    },
    { key: "demo_number", label: "Visit", render: (r) => <span className="num text-xs">{r.demo_number}</span> },
    { key: "customer_name", label: "Customer", render: (r) => <span className="text-sm">{r.customer_name}</span>, hideMobile: true },
    { key: "created_by_name", label: "Claimed by", render: (r) => <span className="text-sm">{r.created_by_name}</span>, hideMobile: true },
    { key: "amount", label: "Amount", align: "right", render: (r) => <span className="num text-sm font-bold">{money(r.amount)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={TONE[r.status]} /> },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[190px]" data-testid="exp-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="year">This year</SelectItem>
            <SelectItem value="all">Everything</SelectItem>
            <SelectItem value="custom">Custom dates</SelectItem>
          </SelectContent>
        </Select>
        {period === "custom" && (
          <>
            <Input type="date" className="w-[160px]" value={custom.from} data-testid="exp-from"
              onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
            <Input type="date" className="w-[160px]" value={custom.to} data-testid="exp-to"
              onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
          </>
        )}
        {data?.rows?.length > 0 && (
          <Button variant="outline" className="ml-auto" data-testid="exp-export"
            onClick={() => {
              downloadCSV(data.rows.map((r) => ({
                Date: r.date, Expense: r.category, Vendor: r.vendor, Details: r.description,
                Visit: r.demo_number, Customer: r.customer_name, "Claimed by": r.created_by_name,
                Amount: r.amount, GST: r.tax, Status: r.status,
              })), `demo-expenses-${data.date_from}-to-${data.date_to}.csv`);
              toast.success("Expense report exported");
            }}>
            <Download className="h-4 w-4" /> Export
          </Button>
        )}
      </div>

      {loading && !data ? (
        <div className="grid h-32 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Bills dated {fdate(data?.date_from)} to {fdate(data?.date_to)}
          </p>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="exp-kpis">
            <StatCard label="Demos" value={t.Demo || 0} currency icon="Presentation" tone="info" />
            <StatCard label="Trainings" value={t.Training || 0} currency icon="GraduationCap" tone="primary" />
            <StatCard label="Total claimed" value={t.total || 0} currency icon="Wallet" tone="warning"
              hint={`${t.count || 0} bill(s)`} />
            <StatCard label="Approved" value={t.approved || 0} currency icon="CheckCircle2" tone="success"
              hint={`${money(t.pending || 0, true)} still pending`} />
          </div>

          <Tabs defaultValue="category">
            <TabsList>
              <TabsTrigger value="category" data-testid="exp-by-category">By category</TabsTrigger>
              <TabsTrigger value="person" data-testid="exp-by-person">By team member</TabsTrigger>
              <TabsTrigger value="customer" data-testid="exp-by-customer">By customer</TabsTrigger>
              <TabsTrigger value="bills" data-testid="exp-bills">All bills</TabsTrigger>
            </TabsList>

            {[["category", "by_category", "Category"], ["person", "by_person", "Team member"],
              ["customer", "by_customer", "Customer"]].map(([v, key, label]) => (
              <TabsContent key={v} value={v} className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-display text-base">Spend by {label.toLowerCase()}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DataTable columns={splitColumns(label)} rows={data?.[key] || []} rowKey="name"
                      empty={<EmptyState icon={Wallet} title="Nothing in this period"
                        description="No demo or training bills were dated in this window." />} />
                  </CardContent>
                </Card>
              </TabsContent>
            ))}

            <TabsContent value="bills" className="mt-4">
              <DataTable columns={billColumns} rows={data?.rows || []}
                empty={<EmptyState icon={Wallet} title="No bills in this period"
                  description="Try a wider date range." />} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};
